import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import AuthContext from '../../context/authContext';
import { currencyFormatter, percentFormatter, decimalFormatter } from '../../util/formatHelper';
import useBranch from '../../hooks/useBranch';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import StaffCostEstimateNotice from '../ui/StaffCostEstimateNotice';
import { STATISTIC_TYPE_STAFF_COST } from '../../constants/statisticTypes';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipContentStyle,
  getRechartsTooltipLabelStyle,
  getStatisticsChartCursorFill,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getRechartsLegendBarProps,
  getStatisticsHtmlThStyle,
} from '../../util/chartTheme';

// Default to the last completed month (e.g. in July the range is 01.06–30.06),
// since the current month is still incomplete and would understate figures.
const defaultStartDate = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
const DEFAULT_EMPLOYER_COST_FACTOR = '1.3';

const formatCurrency = value => currencyFormatter.format(value ?? 0);
const formatPercent = value =>
  value === null || value === undefined ? '–' : `${percentFormatter.format(value)} %`;
const formatHours = value => `${decimalFormatter.format(value ?? 0)} h`;

const REVENUE_SOURCE_LABELS = {
  total: 'Gesamt (POS + Online)',
  pos: 'Nur POS (Kasse)',
  online: 'Nur Online',
};

const StaffCostStatistics = () => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const chartTooltipContentStyle = useMemo(() => getRechartsTooltipContentStyle(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const chartCursorFill = useMemo(() => getStatisticsChartCursorFill(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const tooltipLabelStyle = useMemo(() => getRechartsTooltipLabelStyle(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const revenueColor = theme.palette.primary.main;
  const costColor = theme.palette.error.main;
  const ratioColor = theme.palette.warning.main;

  const cardStyle = useMemo(
    () => ({
      background: statSurfaceBg,
      borderRadius: '8px',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    }),
    [statSurfaceBg]
  );

  const authCtx = useContext(AuthContext);
  const canView =
    authCtx.checkRight('statistic:special:staffCost');

  const { getBranches } = useBranch();
  const getBranchesRef = useRef(getBranches);
  useEffect(() => {
    getBranchesRef.current = getBranches;
  }, [getBranches]);

  const [branchOptions, setBranchOptions] = useState([]);
  const [branchesReady, setBranchesReady] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [revenueSource, setRevenueSource] = useState('total');
  const employerCostFactorRef = useRef(DEFAULT_EMPLOYER_COST_FACTOR);

  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [chartType, setChartType] = useState('bar');
  const [selectedCacheEntryId, setSelectedCacheEntryId] = useState(null);

  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    branchId: '',
    revenueSource: 'total',
    employerCostFactor: DEFAULT_EMPLOYER_COST_FACTOR,
  });

  // Load the branch list for the filter bar (full access — this statistic is
  // cross-branch and admin-gated).
  useEffect(() => {
    let cancelled = false;
    const mapBranch = b => ({
      _id: typeof b._id === 'string' ? b._id : String(b._id),
      label: b.locationName || b.longName || 'Standort',
    });
    (async () => {
      if (!canView) return;
      try {
        const list = await getBranchesRef.current();
        if (cancelled) return;
        setBranchOptions([{ _id: '', label: 'Alle Standorte' }, ...(list || []).map(mapBranch)]);
      } catch (_) {
        if (!cancelled) setBranchOptions([{ _id: '', label: 'Alle Standorte' }]);
      }
      if (!cancelled) setBranchesReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const applyFilters = (nextStart, nextEnd) => {
    const s = nextStart || startDate;
    const e = nextEnd || endDate;
    if (!s || !e || !s.isValid() || !e.isValid()) {
      toast.error('Bitte wähle gültige Daten aus');
      return;
    }
    if (s.isAfter(e)) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
      return;
    }

    const rawFactorInput = employerCostFactorRef.current ? employerCostFactorRef.current.value : '';
    const rawFactor = Number(String(rawFactorInput).replace(',', '.'));
    const employerCostFactor =
      Number.isFinite(rawFactor) && rawFactor > 0 ? String(rawFactor) : DEFAULT_EMPLOYER_COST_FACTOR;

    setSelectedCacheEntryId(null);
    setAppliedFilter({
      startDate: s.format('YYYY-MM-DD'),
      endDate: e.format('YYYY-MM-DD'),
      branchId: selectedBranchId || '',
      revenueSource,
      employerCostFactor,
    });
  };

  const shouldQuery = canView && branchesReady;

  const filterParams = useMemo(() => {
    if (!shouldQuery) return null;
    return {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branches: appliedFilter.branchId || '',
      revenueSource: appliedFilter.revenueSource,
      employerCostFactor: appliedFilter.employerCostFactor,
    };
  }, [appliedFilter, shouldQuery]);

  const job = useStatisticJob({
    statisticType: STATISTIC_TYPE_STAFF_COST,
    endpoint: '/api/v1/statistic/staffCost',
    filterParams,
  });

  const stats = job.payload;

  useEffect(() => {
    if (job.status === 'failed' && job.error) {
      toast.error(`Fehler beim Laden der Personalkosten-Statistik: ${job.error}`);
    }
  }, [job.status, job.error]);

  const { entries: cacheEntries, isLoading: cacheEntriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_STAFF_COST,
  });

  const handleSelectCacheEntry = useCallback(
    entryId => {
      setSelectedCacheEntryId(entryId);
      if (entryId) {
        job.loadCacheEntry(entryId);
      } else {
        job.fetch();
      }
    },
    [job]
  );

  useEffect(() => {
    setStartDate(dayjs(appliedFilter.startDate));
    setEndDate(dayjs(appliedFilter.endDate));
  }, [appliedFilter]);

  const chartData = useMemo(() => {
    if (!stats?.timeSeries?.daily) return [];
    return stats.timeSeries.daily.filter(Boolean).map(point => ({
      ...point,
      label: dayjs(point.date).format('DD.MM.'),
    }));
  }, [stats]);

  const hasChartData = chartData.length > 0;

  const kpiCards = useMemo(() => {
    if (!stats?.totals) return [];
    const t = stats.totals;
    return [
      { label: 'Umsatz gesamt', value: formatCurrency(t.revenue), helper: 'POS + Online im Zeitraum' },
      {
        label: 'Personalkosten gesamt',
        value: formatCurrency(t.staffCost),
        helper: `inkl. Arbeitgeber-Faktor ×${stats?.meta?.employerCostFactor ?? ''}`,
      },
      {
        label: 'Personalkostenquote',
        value: formatPercent(t.costRatioPercent),
        helper: 'Personalkosten im Verhältnis zum Umsatz',
      },
      { label: 'Arbeitsstunden', value: formatHours(t.totalHours), helper: 'Produktive Stunden (L + K)' },
      {
        label: 'Umsatz / Stunde',
        value: t.revenuePerHour === null ? '–' : formatCurrency(t.revenuePerHour),
        helper: 'Umsatz je Personalstunde',
      },
      {
        label: 'Ø Personalkosten / Tag',
        value: formatCurrency(t.averageDailyStaffCost),
        helper: `Über ${t.activeDays} aktive Tage`,
      },
      {
        label: 'Kosten Leitung (L)',
        value: formatCurrency(t.leitungCost),
        helper: `${formatHours(t.leitungHours)} · Arenaleitung`,
      },
      {
        label: 'Kosten Kundenbetreuung (K)',
        value: formatCurrency(t.kundenbetreuungCost),
        helper: `${formatHours(t.kundenbetreuungHours)} · Kundenbetreuung`,
      },
    ];
  }, [stats]);

  if (!canView) {
    return <Navigate to='/welcome' replace />;
  }

  const revenueSourceFilter = (
    <FormControl fullWidth>
      <InputLabel id='staff-cost-revenue-source-label'>Umsatzbasis</InputLabel>
      <Select
        labelId='staff-cost-revenue-source-label'
        label='Umsatzbasis'
        value={revenueSource}
        onChange={event => setRevenueSource(event.target.value)}
      >
        <MenuItem value='total'>{REVENUE_SOURCE_LABELS.total}</MenuItem>
        <MenuItem value='pos'>{REVENUE_SOURCE_LABELS.pos}</MenuItem>
        <MenuItem value='online'>{REVENUE_SOURCE_LABELS.online}</MenuItem>
      </Select>
    </FormControl>
  );

  const employerCostFactorFilter = (
    <TextField
      fullWidth
      type='number'
      label='Arbeitgeber-Faktor'
      defaultValue={DEFAULT_EMPLOYER_COST_FACTOR}
      inputRef={employerCostFactorRef}
      inputProps={{ step: '0.05', min: '1', max: '5' }}
    />
  );

  const renderKpiCards = () => {
    if (!stats) return null;
    return (
      <section>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {kpiCards.map(card => (
            <Box
              key={card.label}
              // flex-basis 220px + minWidth 0 → cards grow to fill, shrink to
              // fit and wrap; they can never overflow the row on narrow screens.
              sx={{ ...cardStyle, flex: '1 1 220px', minWidth: 0, maxWidth: '100%' }}
            >
              <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
              <strong style={{ fontSize: '1.5rem', overflowWrap: 'anywhere' }}>{card.value}</strong>
              <small style={{ color: kpiHelperColor }}>{card.helper}</small>
            </Box>
          ))}
        </Box>
      </section>
    );
  };

  const renderChart = () => {
    if (!stats) return null;
    return (
      <section>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent='space-between'
          sx={{ mb: 1 }}
        >
          <Typography variant='h6' component='h2' sx={{ m: 0 }}>
            Umsatz vs. Personalkosten
          </Typography>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            size='small'
            onChange={(_, value) => value && setChartType(value)}
          >
            <ToggleButton value='bar'>Balken</ToggleButton>
            <ToggleButton value='line'>Linie</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {hasChartData ? (
          <Paper elevation={3} sx={{ p: 2, background: statSurfaceBg, height: 380, minWidth: 0 }}>
            <ResponsiveContainer width='100%' height='100%'>
              <ComposedChart data={chartData}>
                <CartesianGrid stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey='label' stroke={axisStroke} />
                <YAxis
                  yAxisId='euro'
                  stroke={axisStroke}
                  tickFormatter={value => currencyFormatter.format(value)}
                />
                <YAxis
                  yAxisId='percent'
                  orientation='right'
                  stroke={axisStroke}
                  tickFormatter={value => `${value} %`}
                />
                <Tooltip
                  cursor={{ fill: chartCursorFill }}
                  contentStyle={chartTooltipContentStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={(value, name) =>
                    name === 'Personalkostenquote'
                      ? formatPercent(value)
                      : formatCurrency(value)
                  }
                />
                <Legend {...chartLegendBarProps} />
                {chartType === 'bar' ? (
                  <>
                    <Bar yAxisId='euro' dataKey='revenue' name='Umsatz' fill={revenueColor} />
                    <Bar yAxisId='euro' dataKey='staffCost' name='Personalkosten' fill={costColor} />
                  </>
                ) : (
                  <>
                    <Line
                      yAxisId='euro'
                      type='monotone'
                      dataKey='revenue'
                      name='Umsatz'
                      stroke={revenueColor}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId='euro'
                      type='monotone'
                      dataKey='staffCost'
                      name='Personalkosten'
                      stroke={costColor}
                      strokeWidth={2}
                      dot={false}
                    />
                  </>
                )}
                <Line
                  yAxisId='percent'
                  type='monotone'
                  dataKey='costRatioPercent'
                  name='Personalkostenquote'
                  stroke={ratioColor}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Keine Zeitreihendaten verfügbar.
          </Typography>
        )}
      </section>
    );
  };

  const renderBranchTable = () => {
    if (!stats?.byBranch?.length) return null;
    // A single-branch filter yields a one-row table that duplicates the KPIs.
    if (stats.byBranch.length < 2) return null;
    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Nach Standort
        </Typography>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
            <thead>
              <tr>
                <th style={htmlThStyle()}>Standort</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Umsatz</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Personalkosten</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Stunden</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Quote</th>
              </tr>
            </thead>
            <tbody>
              {stats.byBranch.map(branch => (
                <tr key={branch.branchId || branch.branchName}>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                    {branch.branchName}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatCurrency(branch.revenue)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatCurrency(branch.staffCost)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatHours(branch.hours)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatPercent(branch.costRatioPercent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderMethodNote = () => {
    if (!stats?.meta) return null;
    const { defaultHourlyWage, employerCostFactor, shiftsWithAssignedWage, shiftsTotal } = stats.meta;
    return (
      <Paper
        elevation={0}
        sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', background: statSurfaceBg }}
      >
        <Typography variant='body2' color='text.secondary'>
          Personalkosten = gearbeitete Stunden × Stundenlohn × Arbeitgeber-Faktor (×
          {employerCostFactor}). Der Stundenlohn stammt aus dem Mitarbeiterprofil; ohne Zuordnung
          gelten die Defaults: Leitung {formatCurrency(defaultHourlyWage?.leitung)} /h,
          Kundenbetreuung {formatCurrency(defaultHourlyWage?.kundenbetreuung)} /h. In diesem Zeitraum
          hatten {shiftsWithAssignedWage} von {shiftsTotal} Schichten einen individuell hinterlegten
          Stundenlohn.
        </Typography>
      </Paper>
    );
  };

  return (
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      branchOptions={branchOptions}
      selectedBranchId={selectedBranchId}
      onBranchChange={setSelectedBranchId}
      branchSelectIdPrefix='staff-cost-branch'
      extraFilters={[revenueSourceFilter, employerCostFactorFilter]}
      onApply={applyFilters}
      status={job.status}
      isStale={job.isStale}
      cacheComputedAt={job.cacheComputedAt}
      cacheExpiresAt={job.cacheExpiresAt}
      job={job.job}
      error={job.error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedCacheEntryId}
      onSelectEntry={handleSelectCacheEntry}
      entriesLoading={cacheEntriesLoading}
      onRefresh={job.refresh}
      externallyLoading={!branchesReady}
      hasPayload={!!stats}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          // Hard-clip any horizontal overflow from the content (a wide chart or
          // the branch table). Without this a wide child pushes the whole page
          // past the viewport, which makes mobile browsers widen the layout
          // viewport and breaks the shell's responsive breakpoints above (filter
          // bar / status bar then render in their desktop multi-column layout).
          // The branch table keeps its own inner horizontal scroll.
          overflowX: 'clip',
          '& > *': { minWidth: 0, maxWidth: '100%', mb: 3 },
          '& > *:last-child': { mb: 0 },
        }}
      >
        <StaffCostEstimateNotice assumptions={stats?.meta?.assumptions} sx={{ mb: 1 }} />
        {renderKpiCards()}
        {renderChart()}
        {renderBranchTable()}
        {renderMethodNote()}
      </Box>
    </StatisticViewShell>
  );
};

export default StaffCostStatistics;
